import { PayButtonByDevice } from '@/components/PayButtonByDevice/PayButtonByDevice';
import { AdyenGooglePay } from '@/components/Wallets/AdyenGooglePay/AdyenGooglePay';
import { AdyenApplePay } from '@/components/Wallets/AdyenApplePay/AdyenApplePay';
import { NuveiApplePay } from '@/components/Wallets/NuveiApplePay/NuveiApplePay';
import { NuveiGooglePay } from '@/components/Wallets/NuveiGooglePay/NuveiGooglePay';
import { FeatureFlag } from '@/constants/featureFlags';
import type { AvailablePaymentMethod } from '@/types/checkout';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import type { ExpressCheckoutComponentProps } from '@/types/expressCheckout';
import type { ComponentType } from 'react';
import type { Template } from '@/types/template';
import { getDeviceType } from '@/utils/device';
import { expressMethodForDevice, shouldRestrictExpressToNativeWallet } from '@/utils/deviceExpressSupport';

export type { ExpressCheckoutComponentProps } from '@/types/expressCheckout';

export interface ExpressComponentConfig {
  key: string;
  Component: ComponentType<ExpressCheckoutComponentProps>;
  providers: ProviderEnum[];
  paymentMethodNames: PaymentMethodEnum[];
}

const EXPRESS_COMPONENT_CONFIG: ExpressComponentConfig[] = [
  {
    key: 'AdyenApplePay',
    Component: AdyenApplePay,
    providers: [ProviderEnum.ADYEN],
    paymentMethodNames: [PaymentMethodEnum.APPLE_PAY],
  },
  {
    key: 'NuveiApplePay',
    Component: NuveiApplePay,
    providers: [ProviderEnum.NUVEI],
    paymentMethodNames: [PaymentMethodEnum.APPLE_PAY],
  },
  {
    key: 'StripeExpressCheckout',
    Component: PayButtonByDevice,
    providers: [ProviderEnum.STRIPE],
    paymentMethodNames: [PaymentMethodEnum.APPLE_PAY, PaymentMethodEnum.GOOGLE_PAY],
  },
  {
    key: 'AdyenGooglePay',
    Component: AdyenGooglePay,
    providers: [ProviderEnum.ADYEN],
    paymentMethodNames: [PaymentMethodEnum.GOOGLE_PAY],
  },
  {
    key: 'NuveiGooglePay',
    Component: NuveiGooglePay,
    providers: [ProviderEnum.NUVEI],
    paymentMethodNames: [PaymentMethodEnum.GOOGLE_PAY],
  },
  // Chargebee has no express slot: on /subscriptions the wallets are selectable
  // method boxes (see `buildAvailablePaymentMethods`), rendered by
  // {Apple,Google}PayForm like every other provider's box.
];

export const filterExpressComponents = (
  paymentMethods: AvailablePaymentMethod[],
  featureFlags?: Partial<Record<FeatureFlag, boolean>>
): ExpressComponentConfig[] => {
  // Soft kill switch — omit Nuvei wallet express slots when ops set nuvei_wallets=false.
  const nuveiWalletsEnabled = featureFlags?.[FeatureFlag.NUVEI_WALLETS] !== false;
  return EXPRESS_COMPONENT_CONFIG.filter((config) => {
    if (!nuveiWalletsEnabled && config.providers.includes(ProviderEnum.NUVEI)) {
      return false;
    }
    return Boolean(
      paymentMethods.find(
        (pm) =>
          pm.isExpress &&
          config.providers.includes(pm.provider as ProviderEnum) &&
          config.paymentMethodNames.includes(pm.paymentMethodName as PaymentMethodEnum)
      )
    );
  });
};

/**
 * Phone DEFAULT template: keep ONLY the components able to render the
 * device-native wallet (Apple Pay on iOS, Google Pay on Android). This
 * drops the non-native wallet (e.g. Google Pay on iPhone) and PayPal
 * from the express row. Desktop/tablet and non-DEFAULT templates are
 * returned unchanged. The Stripe combined component survives on both
 * iOS and Android (it lists both wallets) and suppresses the non-native
 * wallet internally — see `PayButtonByDevice`.
 */
export const filterExpressComponentsForDevice = (
  components: ExpressComponentConfig[],
  template: Template
): ExpressComponentConfig[] => {
  if (!shouldRestrictExpressToNativeWallet(template)) {
    return components;
  }
  const nativeMethod = expressMethodForDevice(getDeviceType());
  if (!nativeMethod) {
    return components;
  }
  return components.filter((config) => config.paymentMethodNames.includes(nativeMethod));
};
